import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import './Notepad.css';

// ─── Storage ─────────────────────────────────────────────────────────

const STORAGE_KEY = 'notepad_data';

function loadData() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    console.error('Failed to load notepad data:', e);
  }
  return getDefaultData();
}

function saveData(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('Failed to save notepad data:', e);
  }
}

function getDefaultData() {
  return {
    todo: {
      items: [
        { id: '1', text: 'Sample task — click the checkbox to complete', done: false, dueDate: null, createdAt: new Date().toISOString() },
      ],
      notes: '',
    },
    planning: { items: [], notes: '' },
    learning: { items: [], notes: '' },
    ideas: { items: [], notes: '' },
    work: { items: [], notes: '' },
  };
}

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

// ─── Nav ─────────────────────────────────────────────────────────────

const TABS = [
  { key: 'todo', label: 'Todo', path: '/notes' },
  { key: 'planning', label: 'Planning', path: '/notes/planning' },
  { key: 'learning', label: 'Learning', path: '/notes/learning' },
  { key: 'ideas', label: 'Ideas', path: '/notes/ideas' },
  { key: 'work', label: 'Work', path: '/notes/work' },
];

function NotepadNav() {
  const location = useLocation();
  const path = location.pathname;

  const isActive = (to) => {
    if (to === '/notes') return path === '/notes';
    return path.startsWith(to);
  };

  return (
    <nav className="notepad-nav">
      {TABS.map(({ path: to, label }) => (
        <Link key={to} to={to} className={isActive(to) ? 'active' : ''}>
          {label}
        </Link>
      ))}
    </nav>
  );
}

// ─── Toolbar ─────────────────────────────────────────────────────────

function NoteToolbar({ onFormat }) {
  return (
    <div className="notepad-toolbar">
      <button onClick={() => onFormat('bold')} title="Bold"><strong>B</strong></button>
      <button onClick={() => onFormat('italic')} title="Italic"><em>I</em></button>
      <button onClick={() => onFormat('underline')} title="Underline"><u>U</u></button>
      <button onClick={() => onFormat('strikeThrough')} title="Strikethrough"><s>S</s></button>
      <span className="notepad-toolbar-divider" />
      <button onClick={() => onFormat('formatBlock', 'h2')} title="Heading">H</button>
      <button onClick={() => onFormat('formatBlock', 'h3')} title="Subheading">H<sub>2</sub></button>
      <span className="notepad-toolbar-divider" />
      <button onClick={() => onFormat('insertUnorderedList')} title="Bulleted List">&#8226;</button>
      <button onClick={() => onFormat('insertOrderedList')} title="Numbered List">1.</button>
      <button onClick={() => onFormat('formatBlock', 'blockquote')} title="Block Quote">&ldquo;</button>
    </div>
  );
}

// ─── Rich Text Editor ────────────────────────────────────────────────

function NoteEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || '';
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = () => {
    isInternalChange.current = true;
    onChange(editorRef.current.innerHTML);
  };

  const handleFormat = (command, val) => {
    editorRef.current.focus();
    if (command === 'formatBlock') {
      document.execCommand('formatBlock', false, `<${val}>`);
    } else {
      document.execCommand(command, false, val || null);
    }
    handleInput();
  };

  return (
    <div className="notepad-editor-wrapper">
      <NoteToolbar onFormat={handleFormat} />
      <div
        ref={editorRef}
        className="notepad-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        placeholder="Write notes here..."
      />
    </div>
  );
}

// ─── Date Picker ─────────────────────────────────────────────────────

function DueDatePicker({ value, onChange, onRemove }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}`;
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr + 'T00:00:00') < today;
  };

  if (value) {
    return (
      <span className={`notepad-due-badge ${isOverdue(value) ? 'overdue' : ''}`} title={`Due: ${value}`}>
        {formatDate(value)}
        <button className="notepad-due-remove" onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Remove due date">&times;</button>
      </span>
    );
  }

  return (
    <span className="notepad-due-trigger" ref={ref}>
      <button className="notepad-due-btn" onClick={() => setOpen(!open)} title="Set due date">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="2" y="3" width="12" height="11" rx="1.5" />
          <line x1="2" y1="6.5" x2="14" y2="6.5" />
          <line x1="5.5" y1="1.5" x2="5.5" y2="4.5" />
          <line x1="10.5" y1="1.5" x2="10.5" y2="4.5" />
        </svg>
      </button>
      {open && (
        <input
          type="date"
          className="notepad-due-input"
          autoFocus
          onChange={(e) => { onChange(e.target.value); setOpen(false); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
        />
      )}
    </span>
  );
}

// ─── Checklist Item ──────────────────────────────────────────────────

function ChecklistItem({ item, onToggle, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(item.text);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleSave = () => {
    const trimmed = editText.trim();
    if (trimmed) {
      onUpdate({ ...item, text: trimmed });
    }
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') { setEditText(item.text); setEditing(false); }
  };

  return (
    <div className={`notepad-item ${item.done ? 'done' : ''}`}>
      <label className="notepad-checkbox">
        <input type="checkbox" checked={item.done} onChange={() => onToggle(item.id)} />
        <span className="notepad-checkmark" />
      </label>
      {editing ? (
        <input
          ref={inputRef}
          className="notepad-item-edit"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <span className="notepad-item-text" onDoubleClick={() => setEditing(true)}>
          {item.text}
        </span>
      )}
      <div className="notepad-item-actions">
        <DueDatePicker
          value={item.dueDate}
          onChange={(date) => onUpdate({ ...item, dueDate: date })}
          onRemove={() => onUpdate({ ...item, dueDate: null })}
        />
        <button className="notepad-item-delete" onClick={() => onDelete(item.id)} title="Delete">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="4" y1="4" x2="12" y2="12" />
            <line x1="12" y1="4" x2="4" y2="12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Add Item Input ──────────────────────────────────────────────────

function AddItem({ onAdd }) {
  const [text, setText] = useState('');
  const [active, setActive] = useState(false);
  const inputRef = useRef(null);

  const handleAdd = () => {
    const trimmed = text.trim();
    if (trimmed) {
      onAdd(trimmed);
      setText('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleAdd();
    if (e.key === 'Escape') { setText(''); setActive(false); }
  };

  if (!active) {
    return (
      <button className="notepad-add-btn" onClick={() => { setActive(true); setTimeout(() => inputRef.current?.focus(), 0); }}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="8" y1="3" x2="8" y2="13" />
          <line x1="3" y1="8" x2="13" y2="8" />
        </svg>
        Add item
      </button>
    );
  }

  return (
    <div className="notepad-add-row">
      <input
        ref={inputRef}
        className="notepad-add-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { if (!text.trim()) setActive(false); }}
        placeholder="What needs to be done?"
      />
      <button className="notepad-add-confirm" onClick={handleAdd} disabled={!text.trim()}>Add</button>
    </div>
  );
}

// ─── Tab Content ─────────────────────────────────────────────────────

function TabContent({ tabKey, data, setData }) {
  const tabData = data[tabKey] || { items: [], notes: '' };

  const updateTab = useCallback((updater) => {
    setData((prev) => {
      const next = { ...prev, [tabKey]: updater(prev[tabKey] || { items: [], notes: '' }) };
      saveData(next);
      return next;
    });
  }, [tabKey, setData]);

  const addItem = (text) => {
    updateTab((t) => ({
      ...t,
      items: [...t.items, { id: generateId(), text, done: false, dueDate: null, createdAt: new Date().toISOString() }],
    }));
  };

  const toggleItem = (id) => {
    updateTab((t) => ({
      ...t,
      items: t.items.map((i) => (i.id === id ? { ...i, done: !i.done } : i)),
    }));
  };

  const updateItem = (updated) => {
    updateTab((t) => ({
      ...t,
      items: t.items.map((i) => (i.id === updated.id ? updated : i)),
    }));
  };

  const deleteItem = (id) => {
    updateTab((t) => ({
      ...t,
      items: t.items.filter((i) => i.id !== id),
    }));
  };

  const updateNotes = (html) => {
    updateTab((t) => ({ ...t, notes: html }));
  };

  const pending = tabData.items.filter((i) => !i.done);
  const completed = tabData.items.filter((i) => i.done);

  const now = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="notepad-tab-content">
      <div className="notepad-tab-header">
        <h1>{TABS.find((t) => t.key === tabKey)?.label || tabKey}</h1>
        <span className="notepad-date">{now}</span>
      </div>

      {/* Checklist */}
      <div className="notepad-checklist">
        {pending.map((item) => (
          <ChecklistItem key={item.id} item={item} onToggle={toggleItem} onUpdate={updateItem} onDelete={deleteItem} />
        ))}
        <AddItem onAdd={addItem} />
        {completed.length > 0 && (
          <>
            <div className="notepad-completed-divider">
              <span>{completed.length} completed</span>
            </div>
            {completed.map((item) => (
              <ChecklistItem key={item.id} item={item} onToggle={toggleItem} onUpdate={updateItem} onDelete={deleteItem} />
            ))}
          </>
        )}
      </div>

      {/* Rich text notes area */}
      <NoteEditor value={tabData.notes} onChange={updateNotes} />
    </div>
  );
}

// ─── Scroll to top on route change ──────────────────────────────────

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ─── Main Component ─────────────────────────────────────────────────

function Notepad() {
  const [data, setData] = useState(loadData);

  return (
    <div className="notepad-container">
      <ScrollToTop />
      <div className="notepad-theme-toggle">
        <ThemeToggle />
      </div>
      <NotepadNav />
      <Routes>
        <Route path="/" element={<TabContent tabKey="todo" data={data} setData={setData} />} />
        <Route path="/planning" element={<TabContent tabKey="planning" data={data} setData={setData} />} />
        <Route path="/learning" element={<TabContent tabKey="learning" data={data} setData={setData} />} />
        <Route path="/ideas" element={<TabContent tabKey="ideas" data={data} setData={setData} />} />
        <Route path="/work" element={<TabContent tabKey="work" data={data} setData={setData} />} />
      </Routes>
    </div>
  );
}

export default Notepad;
